import React, { useState } from "react";
import { UploadCloud, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Portfolio Mixed-Media V1 — dedicated video upload field.
 *
 * Posts to /admin/upload-portfolio-video (25 MB cap, MP4/WebM only). Kept
 * separate from ImageUploadField and from the older hero-video path so
 * this field can't accidentally widen unrelated upload limits.
 *
 * Purely presentational: parent owns the resolved URL and calls onChange
 * on success. No orientation logic here — that lives in the editor.
 */
export const PortfolioVideoUploadField = ({ value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    if (!/^video\/(mp4|webm)$/.test(file.type)) {
      toast.error("Please choose an MP4 or WebM video");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Video is over 25 MB — please compress it before uploading");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-portfolio-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#141414] border border-[#262626]">
          <div className="w-14 h-14 rounded-lg bg-[#0F0F0F] grid place-items-center">
            <Film className="w-5 h-5 text-[#FF6600]" />
          </div>
          <div className="flex-1 min-w-0 text-xs text-[#A3A3A3] truncate">{value}</div>
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs text-[#FF6600] font-semibold hover:underline"
          >
            Clear
          </button>
        </div>
      ) : (
        <label
          data-testid={`${testId}-dropzone`}
          className={`flex items-center justify-center gap-2 border border-dashed rounded-xl px-4 py-4 cursor-pointer transition-colors ${
            uploading
              ? "border-[#FF6600] bg-[#1A1209]"
              : "border-[#333] hover:border-[#FF6600] hover:bg-[#141414]"
          }`}
        >
          <input
            data-testid={testId}
            type="file"
            accept="video/mp4,video/webm"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <UploadCloud className="w-4 h-4 text-[#A3A3A3]" />
          <span className="text-xs text-white">
            {uploading ? "Uploading…" : "Click to upload video"}
          </span>
        </label>
      )}
      <p className="mt-1.5 text-[10.5px] text-[#7A7A7A] leading-snug">
        Use a web-optimized MP4 or WebM. Maximum 25 MB.
      </p>
    </div>
  );
};
